<template>
  <div :style="computedHeight" :class="{ 'tabbar-page': tabbarActive !== null }">
    <!-- Only display headers and footers when not on the login page -->
    <HeaderDefault v-if="
      $route.name === 'SignUp' ||
      $route.name === 'Login' ||
      $route.name === 'Membership'
    " />

    <HeaderLoggedIn v-if="
      $route.name === 'Home' ||
      $route.name === 'Books' ||
      $route.name === 'InviteManagement' ||
      $route.name === 'Community' ||
      $route.name === 'Library' ||
      $route.name === 'ProfileEdit' ||
      $route.name === 'ExpressionFeedback' ||
      $route.name === 'ExpressionFeedbackDetail' ||
      $route.name === 'Masterclass' ||
      $route.name === 'MasterClassThreads' ||
      $route.name === 'BookAnalytics' ||
      $route.name === 'Payment' ||
      $route.name === 'MembershipsManagement' ||
      $route.name === 'AdminMissions' ||
      $route.name === 'AdminDashboard' ||
      $route.name === 'PaymentManage'
    " />

    <!-- Engine chrome (v5 routing ruling): ONE tab bar for every engine
         surface — tabs and every 더보기 destination alike. Keyed on the
         Engine* route names; /session stays bare (immersive). Legacy
         routes keep their legacy headers untouched until the flip. -->
    <EngineTabBar v-if="tabbarActive !== null" :active="tabbarActive" :desk-only="$route.name === 'EngineChallenge'" />

    <router-view v-slot="{ Component, route }">
      <!-- Keep ONLY the home page alive across navigation, so returning from a
           lesson is instant (no destroy → no refetch → no spinner) and the
           scroll position is preserved. Its key is the stable '/home', so the
           cached instance is reused; every other route keeps its per-path key
           and remounts as before. -->
      <!-- Engine views are kept alive so a tab switch does not tear them down
           and refetch everything. Each one refreshes in activated() instead.
           LibraryPage is DELIBERATELY excluded: it also serves the LIVE
           /library route that members use today, and it gains nothing here
           (0 Firestore round trips warm — its list is bundled JSON). -->
      <keep-alive :include="['HomePage', 'EngineHomeView', 'EngineLessonsView', 'EngineMoreView', 'ChallengesView', 'TodayHomeView']">
        <component :is="Component" v-bind="route.props" :key="$route.fullPath" />
      </keep-alive>
    </router-view>

    <!-- Persistent mini-player: lives above the router so a lesson video handed
         off on navigation keeps playing across routes. -->
    <FloatingVideoPlayer />
  </div>
</template>

<script>
// Importing mobile-specific components
import HeaderDefault from "@/components/HeaderDefault.vue";
import HeaderLoggedIn from "@/components/HeaderLoggedIn.vue";
import EngineTabBar from "@/components/engine/EngineTabBar.vue";
import FloatingVideoPlayer from "@/components/FloatingVideoPlayer.vue";

import { useDisplay } from "vuetify";
import { auth } from "@/firebaseConfig";
import sessionTracking from "@/api/sessionTrackingService";
import pageViewService from "@/api/pageViewService";

export default {
  components: {
    HeaderDefault,
    HeaderLoggedIn,
    EngineTabBar,
    FloatingVideoPlayer,
  },
  computed: {
    /** Which tab the engine bar highlights for this route — null = no bar
     *  (legacy pages, landing/auth, and the immersive /session). */
    tabbarActive() {
      const n = String(this.$route.name || '');
      if (n === 'EngineSession') return null; // immersive, bare
      // 챌린지 is the first tab (2026-10); its page and its list light the same tab
      if (n === 'EngineChallenges' || n === 'EngineChallenge') return 'challenges';
      if (n === 'EngineHome') return 'home';
      if (n === 'Lessons') return 'lessons';
      if (n === 'EngineLibrary') return 'library';
      if (n === 'EngineCommunity') return 'community'; // the fifth tab (ruled 2026-09-05)
      // the mission surfaces belong to 홈 now (이번 주 opens them): with the
      // last tab renamed 프로필, letting them fall through would light the
      // wrong one (ruled 2026-09-14)
      if (n === 'EngineMissions' || n === 'EngineFeedback' || n === 'EngineFeedbackDetail') return 'home';
      // the new 챌린지 program's pages (/today, 새 챌린지 in the beta)
      if (n.startsWith('Today')) return 'today';
      // 프로필 and what hangs off it. No tab carries that name since 2026-10:
      // the bar lights nothing and the avatar takes the ring.
      if (n.startsWith('Engine')) return 'more';
      return null;
    },
  },
  watch: {
    // Every navigation, once auth is known (G3, 2026-09-07 — moved here from
    // the router's afterEach, which fired before auth resolved on a fresh load
    // and dropped the first view of every session).
    '$route.path'(p) { if (auth.currentUser?.uid) pageViewService.log(p); },
  },
  mounted() {
    auth.onAuthStateChanged((user) => {
      if (user?.uid) {
        sessionTracking.start(user.uid);
        // after the router's first resolution — before it, $route.path is "/"
        this.$router.isReady().then(() => pageViewService.log(this.$route.path));
      }
      else sessionTracking.end();
    });
  },
  methods: {
    // Plain block layout. Header sits at the top in normal flow; each routed
    // page handles its own internal scroll via `.scrollable-panel-web`
    // (max-height: 92vh) or equivalent. Previously this used a flex column,
    // but that made short-content pages render as a tiny flex item with
    // black space below — looked like a "blank screen below the header" on
    // every navigation.
    computedHeight() {
      const display = useDisplay();
      const h =
        display.smAndUp.value ||
        this.$route.name === "Landing" ||
        this.$route.name === "Login" ||
        this.$route.name === "SignUp"
          ? "100vh"
          : "95vh";
      // vh first as the fallback, dvh second so capable browsers track the
      // real visible viewport (mobile URL bar) — later declaration wins.
      return `max-height: ${h}; max-height: ${h.replace('vh', 'dvh')};`;
    },
  },
};
</script>

<style>
html,
body {
  margin: 0px;
  overflow: hidden;
  /* Prevents scrolling */
  font-family: var(--font-sans);
  /* Pretendard Variable (via theme.css --font-sans) — one family for KR + EN */
  background-color: var(--bg);
  color: var(--text-primary);
  color-scheme: light;
  /* Light-only app (home revamp) — native UI (scrollbars, form controls,
     pre-CSS canvas) renders light everywhere. */
  word-break: keep-all;
  /* Korean typography standard — never break mid-word (spec §2/§4). */
  max-height: 100vh;
  /* Set the maximum height */
  -ms-overflow-style: none;
  /* IE and Edge */
  scrollbar-width: none;
  /* Firefox */
}

/* Vuetify ships its own Roboto stack on .v-application — override so every
   Vuetify surface inherits the shared token stack (Pretendard) as well. */
.v-application {
  font-family: var(--font-sans) !important;
}

/* Force-dark defeat (spec §6, paid-for lesson): webview/Chrome force-dark
   repaints html/body grounds, so the page ground is a fixed underlay ELEMENT
   with content stacked above — never rely on html/body for the ground color. */
#app::before {
  content: '';
  position: fixed;
  inset: 0;
  background: var(--bg);
  z-index: -1;
}

.loading-circular {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 20%;
}
/* ── Engine tab-bar chrome refit (v5 routing ruling) ─────────────────────
   Legacy pages sized their scroll panels for the ~8vh legacy header. Inside
   the engine chrome the header is gone and the fixed tab bar is in — ONE
   override re-fits them all: desktop = below the 60px top nav, mobile =
   above the bottom bar. Engine views (own 100dvh scroll containers) size
   themselves and ignore these. */
@media (min-width: 768px) {
  .tabbar-page .scrollable-panel-web,
  .tabbar-page .scrollable-panel-mobile {
    height: calc(100dvh - 60px);
    max-height: calc(100dvh - 60px);
    margin-top: 60px;
  }
}
@media (max-width: 767.98px) {
  .tabbar-page .scrollable-panel-web,
  .tabbar-page .scrollable-panel-mobile {
    height: calc(100dvh - 64px - env(safe-area-inset-bottom, 0px));
    max-height: calc(100dvh - 64px - env(safe-area-inset-bottom, 0px));
  }
}
</style>
